'use client'

import { Check, AlertCircle } from 'lucide-react'
import { useTranslations } from 'next-intl'

export interface ToolCall {
  name: string
  status: 'running' | 'ok' | 'error'
  errorMessage?: string
}

/**
 * Maps an underscore_name like "get_brand_stats" to "brand stats".
 * Strips a leading get_/list_/fetch_ prefix, then replaces underscores
 * with spaces (all lowercase — matches mockup style). Fallback label for
 * tools without a `messageList.tools.*` translation.
 */
export function formatToolName(name: string): string {
  return name
    .replace(/^(get_|list_|fetch_)/, '')
    .replace(/_/g, ' ')
}

/**
 * Checklist of the tools an assistant message has called, rendered at the top
 * of the message bubble: one row per call with a spinner while the tool runs,
 * a check once it finishes, and an error mark (with the message on hover) if
 * it failed. Unlike the old chips, the checklist persists after the message
 * completes, so it doubles as a record of how the answer was produced.
 */
export function ToolChecklist({ toolCalls }: { toolCalls?: ToolCall[] }) {
  const t = useTranslations('assistant.messageList')

  if (!toolCalls || toolCalls.length === 0) return null

  return (
    <ul
      style={{
        listStyle: 'none',
        margin: '0 0 0.6rem',
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: '0.35rem',
      }}
    >
      {toolCalls.map((tc, i) => {
        // Friendly label from `messageList.tools.<name>` when the locale has
        // one; otherwise the generic `lookingAt` + formatted tool name, so
        // tools added to the backend later degrade gracefully.
        const labelKey = `tools.${tc.name}`
        const label = t.has(labelKey)
          ? t(labelKey)
          : t('lookingAt', { toolName: formatToolName(tc.name) })
        const running = tc.status === 'running'

        return (
          <li
            key={i}
            title={tc.status === 'error' ? (tc.errorMessage ?? undefined) : undefined}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.45rem',
              fontSize: '0.75rem',
              color: running ? 'var(--color-text-primary)' : 'var(--color-text-muted)',
            }}
          >
            <span
              style={{
                width: '14px',
                height: '14px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {running ? (
                <span className="assistant-tool-spinner" />
              ) : tc.status === 'ok' ? (
                <Check style={{ width: '13px', height: '13px', color: 'var(--color-lime)' }} />
              ) : (
                <AlertCircle style={{ width: '13px', height: '13px', color: 'var(--color-pink)' }} />
              )}
            </span>
            <span className={running ? 'assistant-activity-label' : undefined}>{label}</span>
          </li>
        )
      })}
    </ul>
  )
}
